// src/modules/app-views/_apps/appmulti/quotations/Form/Steps/Modals/PassengerModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Col, DatePicker, Form, Modal, Row } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { returnOnlyNumbers, toCapitalCase } from 'utils/helpers';
import { isValidCpf } from 'utils/validation';
import { generateHexColor } from 'utils/colors';
import translateX from 'utils/translateX';
import {
  SimpleFormProvider,
  useSimpleFormContext,
} from 'components/SimpleComponents/SimpleFormProvider';
import SimpleCpf from 'components/SimpleComponents/SimpleCpf';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleGender from 'components/SimpleComponents/SimpleGender';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import entitiesService from 'services/entitiesService';
import appmultiService from 'modules/app-views/_apps/appmulti/services/appmultiService';
import { API } from 'constants/Api';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const fullWidthStyle = { width: '100%' };
const hiddenButtonStyle = { display: 'none' };
// sentinel placeholder avatar color stamped on un-filled travellers (see Form.jsx
// buildTravellers); when still present we generate a real random color instead.
const PLACEHOLDER_AVATAR_COLOR = ['#', 'b9bcc4'].join('');

// *******************************************************************************
// COMPONENT — body (inside SimpleFormProvider)
// *******************************************************************************
const PassengerModalBody = props => {
  // HOOKS
  const { form } = useSimpleFormContext();

  const {
    record,
    travellers,
    showModal,
    setShowModal,
    onCallbackFn,
    isWorking,
    setSelectedRecord,
    filter,
    plan,
    isDisabled,
    registerOnFinish,
  } = props;

  // STATE
  const [isError, setIsError] = useState(false);
  const [entity, setEntity] = useState(null);
  const [updating, setUpdating] = useState(false);

  const cpfWatch = Form.useWatch('cpf', form);

  // EFFECTS
  // load / reset on open
  useEffect(() => {
    form.resetFields();
    if (record) {
      const rec = { ...record };
      if (rec.birthdate) rec.birthdate = dayjs(rec.birthdate);
      form.setFieldsValue(rec);
      if (record?.entityId) {
        setEntity({
          _id: record.entityId,
          cpf: record?.cpf,
          backColor: record?.backColor,
          foreColor: record?.foreColor,
        });
      }
    } else {
      setEntity(null);
    }
  }, [record, showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // CPF-driven entity lookup (replaces legacy FormCpf onChange)
  useEffect(() => {
    const cpf = returnOnlyNumbers(cpfWatch || '');
    if (!cpf || cpf === entity?.cpf || !isValidCpf(cpf)) return;
    (async () => {
      const doc = await entitiesService.getEntityByField(cpf, 'cpf');
      if (!doc) {
        setEntity(null);
        return;
      }
      setEntity({
        _id: doc?._id,
        cpf,
        foreColor: doc.foreColor,
        backColor: doc.backColor,
      });
      form.setFieldsValue({
        name: doc.name,
        fullName: doc.fullName,
        birthdate: doc?.birthdate ? dayjs(doc.birthdate) : null,
        gender: doc?.gender || null,
        foreColor: doc?.foreColor,
        backColor: doc?.backColor,
      });
      if (doc?.birthdate) onChangeBirthdate(doc.birthdate);
    })();
  }, [cpfWatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // HANDLERS
  const onClose = () => {
    setSelectedRecord(null);
    form.resetFields();
    setShowModal(false);
    setUpdating(false);
  };

  const onChangeBirthdate = value => {
    if (!value) return;
    const limitDate = dayjs(filter.departure).endOf('day');
    const age = limitDate.diff(dayjs(value), 'years');
    form.setFieldValue('age', age);
    if (age < plan.minAge || age > plan.maxAge) {
      appService.message({ flag: 'e', text: 'age_not_allowed' });
      setIsError(true);
    } else {
      setIsError(false);
    }
  };

  const onConfirm = async values => {
    const cpfRaw =
      typeof values.cpf === 'object' && values.cpf !== null ? values.cpf.value : values.cpf;
    const cpf = returnOnlyNumbers(cpfRaw);
    const name = toCapitalCase(values.name);

    if (
      travellers.findIndex(
        x => ((cpf && x.cpf === cpf) || x.name === name) && x.key !== record?.key
      ) > -1
    ) {
      appService.notification({
        flag: 'e',
        title: 'check_info',
        description: 'passenger_already_exists',
        duration: 5,
      });
      return false;
    }

    setUpdating(true);

    if (!entity?._id && cpf) {
      const doc = await entitiesService.getEntityByField(cpf, 'cpf');
      values.entityId = doc?._id;
    } else if (!entity?._id && name) {
      const doc = await entitiesService.getEntityByField(name, 'name');
      values.entityId = doc?._id;
    } else {
      values.entityId = entity?._id;
    }

    values.key = typeof record?.key !== 'undefined' ? record?.key : travellers.length;
    values.cpf = cpf;
    values.name = name;

    const isPlaceholderColor = !entity?.backColor || entity?.backColor === PLACEHOLDER_AVATAR_COLOR;
    const backColor = isPlaceholderColor ? generateHexColor() : entity.backColor;
    const foreColor = isPlaceholderColor ? generateHexColor(backColor, true) : entity.foreColor;
    values.backColor = backColor;
    values.foreColor = foreColor;

    const nameParts = (values.fullName || name || '').trim().split(/\s+/);
    const firstName = nameParts[0] || name;
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined;

    const data = {
      ...values,
      firstName,
      ...(lastName ? { lastName } : {}),
      uniqueKey: cpf || +new Date(),
      dataType: 'person',
      dataProfile: 'traveller',
      entityProfile: ['traveller'],
      name,
    };

    const entityId = values?.entityId;
    const url = API.ENTITIES.URL.replace(':_id?', entityId || '');
    const [doc] = entityId
      ? await axiosService.put({ url, data })
      : await axiosService.post({ url, data });

    values.entityId = doc?._id;
    values.grievanceLevel = appmultiService.getTravelGrievanceLevel(plan, values.age);

    if (onCallbackFn) await onCallbackFn(values);
    onClose();
    return false; // abort SimpleFormProvider auto-save
  };

  // expose onFinish to the provider via a stable handler ref
  registerOnFinish?.(onConfirm);

  // RENDER
  return (
    <Modal
      title={
        <>
          <UserOutlined />
          <span className="ml-2">{translateX('passenger')}</span>
        </>
      }
      destroyOnHidden={true}
      open={showModal}
      width={640}
      okText={translateX(!isDisabled ? 'save' : 'close')}
      onOk={() => (isDisabled ? onClose() : form.submit())}
      onCancel={onClose}
      cancelButtonProps={{ style: hiddenButtonStyle }}
      okButtonProps={{ disabled: isError }}
      getContainer={false}
      okType={isDisabled ? 'default' : 'primary'}
      confirmLoading={isWorking || updating}
    >
      <Row gutter={ROW_GUTTER} className="mt-4">
        <Col xs={24} sm={8}>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- legacy parity: CPF auto-focus on open */}
          <SimpleCpf name="cpf" disabled={isDisabled} required autoFocus />
        </Col>
        <Col xs={24} sm={8}>
          <SimpleInput
            name="name"
            label="name_social"
            disabled={isDisabled || updating}
            required
            onBlur={() => {
              const nameValue = form.getFieldValue('name');
              const fullNameValue = form.getFieldValue('fullName');
              if (nameValue && !fullNameValue) {
                form.setFieldValue('fullName', nameValue);
              }
            }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <SimpleInput name="fullName" label="full_name" disabled={isDisabled || updating} />
        </Col>
      </Row>

      <Row gutter={ROW_GUTTER}>
        <Col xs={18} sm={8}>
          <Form.Item name="birthdate" label={translateX('birthdate')} required>
            <DatePicker
              format="DD/MM/YYYY"
              disabled={isDisabled}
              onChange={onChangeBirthdate}
              style={fullWidthStyle}
            />
          </Form.Item>
        </Col>
        <Col xs={6} sm={4}>
          <SimpleInput name="age" label="age" disabled={true} />
        </Col>
        <Col xs={24} sm={12}>
          <SimpleGender name="gender" disabled={isDisabled} required />
        </Col>
      </Row>
    </Modal>
  );
};

// *******************************************************************************
// COMPONENT — main (wraps the provider)
// *******************************************************************************
const MultiQuotationsOrderPassengerModal = props => {
  // hold the body's onFinish so the provider can call it
  let onFinishHandler = async () => false;
  const registerOnFinish = fn => {
    onFinishHandler = fn;
  };

  return (
    <SimpleFormProvider
      name="form-passenger"
      showSubmitButton={false}
      requiredFields={['cpf', 'name', 'birthdate', 'gender']}
      onFinish={({ values }) => onFinishHandler(values)}
      initialValues={props.record || {}}
    >
      <PassengerModalBody {...props} registerOnFinish={registerOnFinish} />
    </SimpleFormProvider>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MultiQuotationsOrderPassengerModal;
